import PropTypes from "prop-types";
import { EnvironmentOutlined } from "@ant-design/icons";
import { HiDotsHorizontal } from "react-icons/hi";
import PhotoIcon from "../../../assets/icons/photo.svg";
import {
    formatCarLocationLabel,
    getCarPricingSource,
    getCarRateTierDisplay,
    isCarInsuranceLinked,
    MOBILE_CAR_RATE_TIERS,
} from "../utils/carDisplayHelpers";
import { RowActionsMenu } from "../../../components/data-table";

function CarCard({ car, t, isCarPricingMode, actionItems, onActionClick }) {
    const locationLabel = formatCarLocationLabel(car);
    const insuranceLinked = isCarInsuranceLinked(car);
    const pricingSource = getCarPricingSource(car, isCarPricingMode);

    const subtitle = car?.carNumber || t("notAvailable");

    const coverageBadgeClass = insuranceLinked
        ? "bg-[#EEF2FF] text-[#5D5DF6]"
        : "bg-[#FEF2F2] text-[#DC2626]";

    return (
        <article className="overflow-hidden rounded-2xl border border-[#E5E7EB]/80 bg-white shadow-sm ring-1 ring-black/[0.03]">
            <div className="relative flex items-stretch gap-3 p-3.5">
                <div className="flex shrink-0 self-stretch">
                    <div
                        className="aspect-square h-full min-h-[5.25rem] w-auto min-w-[5.25rem] max-w-[6.5rem] overflow-hidden rounded-xl bg-[#F2F4F7] ring-1 ring-black/[0.04]">
                        {car?.images?.image1 ? (
                            <img src={car.images.image1} alt="" className="h-full w-full object-cover" />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center">
                                <img src={PhotoIcon} alt="" className="h-8 w-8 opacity-40" />
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 self-stretch pr-9">
                    <h3 className="mb-2 block w-full truncate p-0 text-left text-[15px] font-semibold leading-none tracking-tight text-[#2F2F46]">
                        {car?.carName || t("notAvailable")}
                    </h3>
                    <p className="m-0 w-full truncate text-left text-xs font-medium leading-tight text-[#667085]">
                        {subtitle}
                    </p>
                    {locationLabel ? (
                        <div className="flex w-full min-w-0 items-start gap-1.5 text-left text-xs leading-snug text-[#667085]">
                            <span
                                className="mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#F2F4F7] text-[#98A2B3]"
                                aria-hidden>
                                <EnvironmentOutlined className="text-[10px]" />
                            </span>
                            <span className="line-clamp-2 min-w-0">{locationLabel}</span>
                        </div>
                    ) : null}
                    <span
                        className={`inline-flex max-w-fit items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${coverageBadgeClass}`}>
                        {insuranceLinked
                            ? t("mobileList.insuranceLinkedBadge")
                            : t("mobileList.noCoverageBadge")}
                    </span>
                </div>

                <div className="absolute right-2 top-2">
                    <RowActionsMenu
                        items={actionItems}
                        onClick={({ key }) => onActionClick(key, car)}
                        label={t("action")}
                        icon={<HiDotsHorizontal className="text-lg" />}
                        className="!h-8 !w-8 !rounded-full text-[#667085]"
                    />
                </div>
            </div>

            <div className="grid grid-cols-4 gap-1 border-t border-[#EEF0F6] bg-[#FAFBFC] px-3 py-2.5">
                {MOBILE_CAR_RATE_TIERS.map(({ key, titleKey }) => {
                    const { label, isDisabled } = getCarRateTierDisplay(pricingSource, key, t);
                    return (
                        <div key={key} className="min-w-0 px-0.5 text-center">
                            <p className="text-[10px] font-medium uppercase tracking-wider text-[#98A2B3]">
                                {t(titleKey)}
                            </p>
                            <p
                                className={`mt-0.5 truncate text-sm font-semibold tabular-nums ${
                                    isDisabled ? "text-[#C4C9D4]" : "text-[#2F2F46]"
                                }`}>
                                {label}
                            </p>
                        </div>
                    );
                })}
            </div>
        </article>
    );
}

CarCard.propTypes = {
    car: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
    isCarPricingMode: PropTypes.bool.isRequired,
    actionItems: PropTypes.arrayOf(PropTypes.object).isRequired,
    onActionClick: PropTypes.func.isRequired,
};

export default CarCard;
